/**
 * Shared presentational primitives for the console. Discrete controls are real
 * Oxygen components (Tag, Label, SkeletonBlock, Button, Icon); the surrounding
 * surfaces are styled-components that read Oxygen *semantic tokens* only — so the
 * whole console themes for light and dark with no raw hex.
 */

import type { ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';
import Tag from '@8x8/oxygen-tag';
import Button from '@8x8/oxygen-button';
import { SkeletonBlock } from '@8x8/oxygen-skeletons';
import { ExclamationCircleIcon, InfoCircleIcon } from '@8x8/oxygen-icon';
import type { DeviceRole, Environment, RoomHealth, RoomStatus } from '@spaces/contracts';

/* ---- surfaces ---------------------------------------------------------- */

/** A white (light) / near-black (dark) card surface — the standard panel. */
export const Card = styled.section`
    background: ${p => p.theme.ui06};
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 8px;
    padding: 20px;
`;

export const CardTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${p => p.theme.textColor01};
    margin-bottom: 16px;
`;

export const PageTitle = styled.h1`
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
    letter-spacing: -0.5px;
    color: ${p => p.theme.textColor01};
    margin: 0;
`;

export const Muted = styled.span`
    color: ${p => p.theme.textColor02};
`;

export const Mono = styled.span`
    font-family: ui-monospace, Menlo, Consolas, monospace;
    color: ${p => p.theme.textColor02};
`;

/* ---- status + role tags (stock Oxygen Tag) ----------------------------- */

const STATUS_VARIANT: Record<RoomStatus, 'light' | 'dark' | 'blue' | 'yellow' | 'red'> = {
    active: 'blue',
    provisioning: 'yellow',
    disabled: 'light',
    archived: 'dark'
};

const STATUS_LABEL: Record<RoomStatus, string> = {
    active: 'Active',
    provisioning: 'Provisioning',
    disabled: 'Disabled',
    archived: 'Archived'
};

/** Room-lifecycle chip. Mapping: active→blue, provisioning→yellow, disabled→light, archived→dark. */
export function StatusTag({ status }: { status: RoomStatus }): JSX.Element {
    return <Tag variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Tag>;
}

const ROLE_LABEL: Record<DeviceRole, string> = {
    'spot-tv': 'TV',
    'remote-perm': 'Remote',
    'remote-temp': 'Temp remote'
};

/** Device-role chip — neutral (roles are not colour-coded like statuses). */
export function DeviceRoleTag({ role }: { role: DeviceRole }): JSX.Element {
    return <Tag variant={role === 'spot-tv' ? 'dark' : 'light'}>{ROLE_LABEL[role]}</Tag>;
}

/** Environment chip in the top bar / login (DEV / STAGING / PROD). */
export function EnvTag({ env }: { env: Environment }): JSX.Element {
    const variant = env === 'prod' ? 'red' : env === 'staging' ? 'blue' : 'yellow';

    return <Tag variant={variant}>{env.toUpperCase()}</Tag>;
}

/* ---- room health indicator (live: green / amber / red / neutral) ------- */

export type HealthTone = 'online' | 'degraded' | 'offline' | 'idle' | 'unknown';

/**
 * Reduce a {@link RoomHealth} to a display tone. GREEN only when the TV is actually
 * online; AMBER for a stale heartbeat (degraded); RED for offline/error; a healthy-
 * but-TV-absent room (e.g. disabled/archived) is neutral "idle", not a false green.
 */
export function healthTone(health?: RoomHealth): HealthTone {
    if (!health) {
        return 'unknown';
    }
    if (health.status === 'error') {
        return 'offline';
    }
    if (health.status === 'degraded') {
        return 'degraded';
    }

    return health.tvOnline ? 'online' : 'idle';
}

const TONE_LABEL: Record<HealthTone, string> = {
    online: 'Online',
    degraded: 'Stale',
    offline: 'Offline',
    idle: 'No TV',
    unknown: 'Unknown'
};

const Dot = styled.span<{ $color: string; $pulse: boolean }>`
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${p => p.$color};
    box-shadow: 0 0 0 ${p => (p.$pulse ? '3px' : '0')} ${p => `${p.$color}33`};
`;

const HealthRow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12.5px;
    font-weight: 500;
    color: ${p => p.theme.textColor01};
`;

/** A colored dot + label for a room's live health. Used in the table + detail. */
export function HealthIndicator({ health }: { health?: RoomHealth }): JSX.Element {
    const theme = useTheme();
    const tone = healthTone(health);
    const color = tone === 'online'
        ? theme.success01
        : tone === 'degraded'
            ? theme.warning03
            : tone === 'offline'
                ? theme.error01
                : theme.textColor05;

    return (
        <HealthRow>
            <Dot $color={color} $pulse={tone === 'online'} />
            {TONE_LABEL[tone]}
        </HealthRow>
    );
}

/* ---- "planned" chip ---------------------------------------------------- */

export const PlannedChip = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 1000px;
    background: ${p => p.theme.ui31};
    color: ${p => p.theme.textColor05};
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
`;

/* ---- inline notices (info / warning) ----------------------------------- */

const NoticeBox = styled.div<{ $bg: string; $border: string; $fg: string }>`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 11px 14px;
    border-radius: 6px;
    background: ${p => p.$bg};
    border: 1px solid ${p => p.$border};
    color: ${p => p.$fg};
    font-size: 12.5px;
    line-height: 18px;
`;

/** A standing inline notice. AlertBanner has a single fixed style and no severity
 * prop, so the design's distinct info/warning notices are composed from Oxygen
 * tokens + an Oxygen Icon (info → brand-tinted, warning → yellow). */
export function Notice({ kind, children }: { kind: 'info' | 'warning'; children: ReactNode }): JSX.Element {
    const theme = useTheme();
    const isWarn = kind === 'warning';
    const accent = isWarn ? theme.warning03 : theme.action08;
    const bg = isWarn ? theme.ui17 : `${theme.action08}14`;
    const border = isWarn ? `${theme.warning03}55` : `${theme.action08}33`;
    const Glyph = isWarn ? ExclamationCircleIcon : InfoCircleIcon;

    return (
        <NoticeBox $bg={bg} $border={border} $fg={isWarn ? theme.warning03 : theme.textColor01}>
            <span style={{ flexShrink: 0, color: accent, display: 'inline-flex', marginTop: 1 }}>
                <Glyph size={16} />
            </span>
            <span>{children}</span>
        </NoticeBox>
    );
}

/* ---- form field label -------------------------------------------------- */

export const FieldLabel = styled.label`
    display: block;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: ${p => p.theme.textColor01};
    margin-bottom: 6px;
`;

export const FieldHint = styled.div`
    font-size: 12px;
    color: ${p => p.theme.textColor05};
    margin-top: 5px;
`;

export const RequiredMark = styled.span`
    color: ${p => p.theme.error01};
`;

/* ---- skeleton + empty states ------------------------------------------- */

const SkelRow = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 13px 16px;
    border-bottom: 1px solid ${p => p.theme.ui01};
`;

/** A sized Oxygen skeleton block (Oxygen sizes via `style`, not w/h props). */
export function Skel({ w, h = 14 }: { w: string | number; h?: string | number }): JSX.Element {
    return <SkeletonBlock style={{ width: w, height: h, borderRadius: 4 }} />;
}

/** Loading placeholder rows for a table (Oxygen SkeletonBlock). */
export function SkeletonRows({ rows = 5 }: { rows?: number }): JSX.Element {
    return (
        <div>
            {Array.from({ length: rows }, (_, i) => (
                <SkelRow key={i}>
                    <div style={{ flex: 2 }}>
                        <Skel w="60%" h={14} />
                        <div style={{ height: 6 }} />
                        <Skel w="38%" h={11} />
                    </div>
                    <div style={{ flex: 1 }}><Skel w="50%" h={12} /></div>
                    <div style={{ flex: 1 }}><Skel w="70px" h={20} /></div>
                    <div style={{ flex: 1 }}><Skel w="40%" h={12} /></div>
                </SkelRow>
            ))}
        </div>
    );
}

const EmptyWrap = styled.div`
    text-align: center;
    padding: 48px 24px;
    color: ${p => p.theme.textColor02};
`;

const EmptyIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 1000px;
    background: ${p => `${p.theme.action08}14`};
    color: ${p => p.theme.action08};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
`;

const EmptyHeading = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${p => p.theme.textColor01};
`;

const EmptyBody = styled.div`
    font-size: 14px;
    color: ${p => p.theme.textColor02};
    margin-top: 6px;
    max-width: 380px;
    margin-left: auto;
    margin-right: auto;
`;

/** A modest empty-state composition (icon + heading + body + optional action). */
export function EmptyState(
    { icon, heading, body, actionLabel, onAction }:
    { icon?: ReactNode; heading: string; body?: string; actionLabel?: string; onAction?: () => void }
): JSX.Element {
    return (
        <EmptyWrap>
            {icon ? <EmptyIcon>{icon}</EmptyIcon> : null}
            <EmptyHeading>{heading}</EmptyHeading>
            {body ? <EmptyBody>{body}</EmptyBody> : null}
            {actionLabel && onAction ? (
                <div style={{ marginTop: 20 }}>
                    <Button variant="primary" onClick={onAction}>{actionLabel}</Button>
                </div>
            ) : null}
        </EmptyWrap>
    );
}
